import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";

import {
  CASE_GONE_SENTENCE,
  CASE_KEY,
  CASES_KEY,
  caseGone,
  ProblemError,
  RECORD_KEY,
  refusalCode,
  retractEntry,
  type CorrespondenceOut,
} from "@/api";
import { Button } from "@/components/Button";
import { Dialog, DialogFooter } from "@/components/Dialog";
import { TextArea } from "@/components/fields/TextArea";
import { StateMessage } from "@/components/StateMessage";

import styles from "./Record.module.css";

interface RetractDialogProps {
  caseId: string;
  entry: CorrespondenceOut;
  onClose: () => void;
  onDone: (sentence: string) => void;
  /** Once the dialog is gone and the focus is back on its opener (`Dialog.onClosed`). */
  onClosed?: () => void;
}

/**
 * „Löschen" is „Zurückziehen" (design §4.2): the entry stays in the record,
 * marked, with the reason, and the file stays in storage — the table is
 * append-only for the owner too. The reason is required here — at the field
 * (UX ruling 8) — and again on the server (422, blank and NUL-only too,
 * Plan B). The reason stays on the entry; the audit trail records only that
 * there was one (`{"hasReason": true}`, Plan B).
 *
 * While the request is out the button keeps the focus (`aria-disabled`,
 * „Wird zurückgezogen …") and Escape does not close (`busy` on the Dialog):
 * the reason typed is not thrown away mid-request (record fix wave I1, T9).
 *
 * A Vorgang handed over while the dialog was open answers 404 (final review
 * of Plan D, I-3): the Vorgang is read again — the record turns into „nicht
 * gefunden" — and until then the dialog says why and holds its button, with
 * that sentence as the reason. „Abbrechen" stays.
 */
export function RetractDialog({ caseId, entry, onClose, onDone, onClosed }: RetractDialogProps) {
  const client = useQueryClient();
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [gone, setGone] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || gone) return;
    const why = reason.split("\u0000").join("").trim();
    if (why === "") {
      setError("Ohne Grund lässt sich nichts zurückziehen.");
      fieldRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    setRefused(null);
    try {
      await retractEntry(caseId, entry.id, why);
      void client.invalidateQueries({ queryKey: CASES_KEY });
      await client.invalidateQueries({ queryKey: RECORD_KEY(caseId) });
      onDone("Zurückgezogen. Der Eintrag bleibt in der Akte.");
    } catch (cause) {
      setBusy(false);
      if (caseGone(cause)) {
        setGone(true);
        setRefused(CASE_GONE_SENTENCE);
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      } else if (refusalCode(cause) === "retracted") {
        setRefused("Dieser Eintrag ist schon zurückgezogen.");
        void client.invalidateQueries({ queryKey: RECORD_KEY(caseId) });
      } else {
        setRefused(cause instanceof ProblemError ? cause.userMessage : "Nicht zurückgezogen — Server nicht erreichbar.");
      }
      // No field is wrong — the focus goes back to the button (record fix wave I1).
      submitRef.current?.focus();
    }
  }

  return (
    <Dialog
      title="Eintrag zurückziehen"
      subtitle={entry.subject ?? entry.sourceFilename ?? "Notiz"}
      onClose={onClose}
      onClosed={onClosed}
      busy={busy}
    >
      <form className={styles.dialogForm} noValidate onSubmit={(event) => void submit(event)}>
        <p className={styles.plain}>Der Eintrag bleibt in der Akte, mit deinem Grund. Löschen lässt er sich nicht.</p>
        <TextArea
          id="retract-reason"
          label="Grund (Pflicht)"
          value={reason}
          onChange={(text) => {
            setError(null);
            setReason(text);
          }}
          rows={3}
          maxLength={500}
          textareaRef={fieldRef}
          error={error}
        />
        {/* The server's refusal in the one notice pattern (final review of
            Plan D) — red edge, symbol and word, the only `role="alert"` here. */}
        {refused !== null ? <StateMessage id="retract-refused" tone="conflict" live="alert" title={refused} /> : null}
        <DialogFooter>
          <Button onClick={onClose} aria-disabled={busy ? "true" : undefined}>
            Abbrechen
          </Button>
          <Button
            ref={submitRef}
            type="submit"
            variant="primary"
            busy={busy}
            aria-disabled={busy || gone ? "true" : undefined}
            aria-describedby={gone ? "retract-refused" : undefined}
          >
            {busy ? "Wird zurückgezogen …" : "Eintrag zurückziehen"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
